import { Controller } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { fr } from 'date-fns/locale';


const PersonalInfoPage = ({
  register,
  errors,
  backendErrors,
  control,
  watch,
  setValue,
  validEmail,
  validDob,
  customErrors,
  isFieldRequired,
  shouldShowField,
  personalDisabled,
  commonDisabled,
  AccountFormService,
  setValidEmail,
  setValidDob,
  setCustomErrors,
  hasError,
  getErrorMessage,
  isManagedAccount
}) => {
   
  const isMinorWithRepresentative = () => {
    const dob = watch("dob");
    const isManagedAccount = watch("IsManagedAccount");
    const representativeIds = watch("RepresentativeAccountIds");
    
    if (!dob || !isManagedAccount || !representativeIds || representativeIds.length === 0) {
      return false;
    }
    
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age < 18;
  };

  const minorWithRep = isMinorWithRepresentative();
  
  const investmentOptions = [
    { value: 'Obligations', label: 'Obligations' },
    { value: 'Actions', label: 'Actions' },
    { value: 'monétaire', label: 'Monétaire' },
    { value: 'derivés', label: 'Dérivés' }
  ];

  return (
    <div className="page-content">
      <div className="page-header mb-4">
        <h2 className="h4 mb-2">Informations Personnelles</h2>
        <p className="text-muted">
          Renseignez les informations personnelles du titulaire du compte.
        </p>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row">
                {shouldShowField('email') && !isManagedAccount && (
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">
                        Addresse mail {isFieldRequired('email') && <span className="text-danger">*</span>}
                      </label>
                      <input
                        type="email"
                        {...register("email", { 
                          required: isFieldRequired('email') && !isManagedAccount ? "Ce champ est requis." : false 
                        })}
                        onChange={(e) => {
                          setValue("email", e.target.value);
                          AccountFormService.validateEmail(e.target.value, setValidEmail, setCustomErrors);
                        }}
                        className={`form-control ${
                          errors.email || backendErrors.email || hasError('email') ? 'is-invalid' : ''
                        } ${validEmail && !hasError('email') ? 'is-valid' : ''}`}
                        id="email"
                        disabled={commonDisabled || isManagedAccount}
                      />
                      {(errors.email || hasError('email')) && (
                        <div className="invalid-feedback">
                          {errors.email?.message || getErrorMessage('email')}
                        </div>
                      )}
                      {validEmail && !customErrors.email && !hasError('email') && (
                        <div className="valid-feedback">Email valide</div>
                      )}
                      {isManagedAccount && (
                        <div className="form-text text-info">
                          <i className="fas fa-info-circle me-1"></i>
                          L'email n'est pas requis pour un compte administré.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="phoneNumber" className="form-label">
                      Numéro de Téléphone {isFieldRequired('phoneNumber') && <span className="text-danger">*</span>}
                    </label>
                    <PhoneInput
                      country={'cm'}
                      value={watch("phoneNumber")}
                      onChange={phone => {
                        if (phone.startsWith("+237")) {
                          const local = phone.replace(/^\+237/, '').replace(/\D/g, '');
                          if (local.startsWith("6") && local.length > 9) return;
                          if (/^[2-5]/.test(local) && local.length > 8) return;
                        }
                        setValue("phoneNumber", phone);
                      }}
                      inputProps={{
                        name: 'phoneNumber',
                        required: isFieldRequired('phoneNumber'),
                        placeholder: 'Ex : 6 xx xx xx xx',
                        className: `form-control ${
                          errors.phoneNumber || backendErrors.phoneNumber || hasError('phoneNumber') ? 'is-invalid' : ''
                        } ${watch("phoneNumber") && !hasError('phoneNumber') ? 'is-valid' : ''}`,
                        disabled: commonDisabled,
                        id: 'phoneNumber'
                      }}
                      masks={{cm: '... ... .. ..'}}
                      specialLabel="" 
                    />
                    {(errors.phoneNumber || hasError('phoneNumber')) && (
                      <div className="invalid-feedback">
                        {errors.phoneNumber?.message || getErrorMessage('phoneNumber')}
                      </div>
                    )}

                    {(() => {
                      const phone = watch("phoneNumber") || "";
                      if (phone.startsWith("+237")) {
                        const local = phone.replace(/^\+237/, '').replace(/\D/g, '');
                        if (local.length > 0) {
                          if (local.startsWith("6")) {
                            if (local.length > 9) {
                              return <div className="text-danger small">Numéro Orange/MTN : 9 chiffres commençant par 6.</div>;
                            } else if (local.length < 9) {
                              return <div className="text-warning small">Numéro Orange/MTN incomplet : 9 chiffres requis.</div>;
                            }
                          } else if (/^[2-5]/.test(local)) {
                            if (local.length > 8) {
                              return <div className="text-danger small">Numéro Camtel : 8 chiffres commençant par 2, 3, 4 ou 5.</div>;
                            } else if (local.length < 8) {
                              return <div className="text-warning small">Numéro Camtel incomplet : 8 chiffres requis.</div>;
                            }
                          } else {
                            return <div className="text-danger small">Numéro non valide pour le Cameroun.</div>;
                          }
                        }
                      }
                      return null;
                    })()}
                  </div>
                </div>
              </div>

              <div className="row">
                {shouldShowField('firstName') && (
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="firstName" className="form-label">
                        Noms {isFieldRequired('firstName') && <span className="text-danger">*</span>}
                      </label>
                      <input
                        type="text"
                        {...register("firstName", { required: isFieldRequired('firstName') ? "Ce champ est requis." : false })}
                        className={`form-control ${
                          errors.firstName || backendErrors.firstName || hasError('firstName') ? 'is-invalid' : ''
                        } ${watch("firstName") && !hasError('firstName') ? 'is-valid' : ''}`}
                        id="firstName"
                        disabled={personalDisabled}
                      />
                      {(errors.firstName || hasError('firstName')) && (
                        <div className="invalid-feedback">
                          {errors.firstName?.message || getErrorMessage('firstName')}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {shouldShowField('lastName') && (
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="lastName" className="form-label">
                        Prenom {isFieldRequired('lastName') && <span className="text-danger">*</span>}
                      </label>
                      <input
                        type="text"
                        {...register("lastName", { required: isFieldRequired('lastName') ? "Ce champ est requis." : false })}
                        className={`form-control ${
                          errors.lastName || backendErrors.lastName || hasError('lastName') ? 'is-invalid' : ''
                        } ${watch("lastName") && !hasError('lastName') ? 'is-valid' : ''}`}
                        id="lastName"
                        disabled={personalDisabled}
                      />
                      {(errors.lastName || hasError('lastName')) && (
                        <div className="invalid-feedback">
                          {errors.lastName?.message || getErrorMessage('lastName')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {shouldShowField('dob') && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="dob" className="form-label">
                        Date de naissance {isFieldRequired('dob') && <span className="text-danger">*</span>}
                      </label>
                      
                      <Controller
                        name="dob"
                        control={control}
                        rules={{ 
                          required: isFieldRequired('dob') ? "Ce champ est requis." : false 
                        }}
                        render={({ field }) => (
                          <DatePicker
                            selected={field.value ? new Date(field.value) : null}
                            onChange={(date) => {
                              const formattedDate = date ? date.toISOString().split('T')[0] : '';
                              field.onChange(formattedDate);
                              setValue("dob", formattedDate);
                              
                              if (formattedDate) {
                                setValidDob(true);
                                setCustomErrors(prev => ({ ...prev, dob: '' }));
                              } else {
                                setValidDob(false);
                              }
                            }}
                            dateFormat="dd/MM/yyyy"
                            locale={fr}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={100}
                            maxDate={new Date()}
                            placeholderText="JJ/MM/AAAA"
                            disabled={personalDisabled}
                            className={`form-control ${
                              errors.dob || backendErrors.dob || hasError('dob') ? 'is-invalid' : ''
                            } ${validDob && !hasError('dob') ? 'is-valid' : ''}`}
                            autoComplete="off"
                            showMonthDropdown
                          />
                        )}
                      />
                      
                      {watch("dob") && !isManagedAccount && (() => {
                        const dob = watch("dob");
                        if (dob) {
                          const birthDate = new Date(dob);
                          const today = new Date();
                          let age = today.getFullYear() - birthDate.getFullYear();
                          const m = today.getMonth() - birthDate.getMonth();
                          if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                            age--;
                          }
                          
                          if (age < 18) {
                            return (
                              <div className="alert alert-warning mt-2">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                <strong>Utilisateur mineur détecté ({age} ans)</strong>
                                <div className="small">
                                  Pour les personnes de moins de 18 ans, un représentant légal est obligatoire.
                                  <button 
                                    type="button"
                                    className="btn btn-sm btn-outline-primary ms-2"
                                    onClick={() => {
                                      setValue("IsManagedAccount", true);
                                      document.querySelector('.card.border-primary')?.scrollIntoView({ 
                                        behavior: 'smooth' 
                                      });
                                    }}
                                  >
                                    <i className="fas fa-user-shield me-1"></i>
                                    Activer le mode compte administré
                                  </button>
                                </div>
                              </div>
                            );
                          }
                        }
                        return null;
                      })()}
                      
                      <small className="text-muted">
                        💡 Tapez directement la date (JJ/MM/AAAA) ou cliquez pour sélectionner
                      </small>
                      
                      {(errors.dob || hasError('dob')) && (
                        <div className="invalid-feedback d-block">
                          {errors.dob?.message || getErrorMessage('dob')}
                        </div>
                      )}
                      {validDob && !customErrors.dob && !hasError('dob') && (
                        <div className="valid-feedback">Âge valide</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {shouldShowField('address') && (
                <div className="mb-3">
                  <label htmlFor="address" className="form-label">
                    Adresse {isFieldRequired('address') && <span className="text-danger">*</span>}
                  </label>
                  <input
                    type="text"
                    {...register("address", { required: isFieldRequired('address') ? "Ce champ est requis." : false })}
                    className={`form-control ${
                      errors.address || backendErrors.address || hasError('address') ? 'is-invalid' : ''
                    } ${watch("address") && !hasError('address') ? 'is-valid' : ''}`}
                    id="address"
                    disabled={commonDisabled}
                  />
                  {(errors.address || hasError('address')) && (
                    <div className="invalid-feedback">
                      {errors.address?.message || getErrorMessage('address')}
                    </div>
                  )}
                </div>
              )}

              <div className="row">
                {shouldShowField('countryOfBirth') && (
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="countryOfBirth" className="form-label">
                        Pays de naissance {isFieldRequired('countryOfBirth') && <span className="text-danger">*</span>}
                      </label>
                      <input
                        type="text"
                        {...register("countryOfBirth", { required: isFieldRequired('countryOfBirth') ? "Ce champ est requis." : false })}
                        className={`form-control ${
                          errors.countryOfBirth || backendErrors.countryOfBirth || hasError('countryOfBirth') ? 'is-invalid' : ''
                        } ${watch("countryOfBirth") && !hasError('countryOfBirth') ? 'is-valid' : ''}`}
                        id="countryOfBirth"
                        disabled={personalDisabled}
                      />
                      {(errors.countryOfBirth || hasError('countryOfBirth')) && (
                        <div className="invalid-feedback">
                          {errors.countryOfBirth?.message || getErrorMessage('countryOfBirth')}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {shouldShowField('cityOfBirth') && (
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="cityOfBirth" className="form-label">
                        Ville de naissance {isFieldRequired('cityOfBirth') && <span className="text-danger">*</span>}
                      </label>
                      <input
                        type="text"
                        {...register("cityOfBirth", { required: isFieldRequired('cityOfBirth') ? "Ce champ est requis." : false })}
                        className={`form-control ${
                          errors.cityOfBirth || backendErrors.cityOfBirth || hasError('cityOfBirth') ? 'is-invalid' : ''
                        } ${watch("cityOfBirth") && !hasError('cityOfBirth') ? 'is-valid' : ''}`}
                        id="cityOfBirth"
                        disabled={personalDisabled}
                      />
                      {(errors.cityOfBirth || hasError('cityOfBirth')) && (
                        <div className="invalid-feedback">
                          {errors.cityOfBirth?.message || getErrorMessage('cityOfBirth')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {shouldShowField('job') && (
                <div className="mb-3">
                  <label htmlFor="job" className="form-label">Profession</label>
                  <input
                    type="text"
                    {...register("job")}
                    className={`form-control ${
                      errors.job || backendErrors.job || hasError('job') ? 'is-invalid' : ''
                    } ${watch("job") && !hasError('job') ? 'is-valid' : ''}`}
                    id="job"
                    disabled={commonDisabled}
                  />
                  {(errors.job || hasError('job')) && (
                    <div className="invalid-feedback">
                      {errors.job?.message || getErrorMessage('job')}
                    </div>
                  )}
                </div>
              )}

              {shouldShowField('tva') && (
                <div className="mb-3">
                  <label htmlFor="tva" className="form-label">
                    TVA (Taxe sur la Valeur Ajoutée)
                  </label>
                  <input
                    type="text"
                    {...register("tva")}
                    className={`form-control ${
                      errors.tva || backendErrors.tva || hasError('tva') ? 'is-invalid' : ''
                    } ${watch("tva") && !hasError('tva') ? 'is-valid' : ''}`}
                    id="tva"
                    disabled={commonDisabled}
                  />
                  <div className="form-text">
                    <i className="fas fa-info-circle me-1"></i>
                    La TVA (Taxe sur la Valeur Ajoutée) est un impôt ajouté au prix des biens 
                    et services. Le client la paie lors de l'achat, et l'entreprise la collecte 
                    pour la reverser à l'État. Renseignez ce champ uniquement si votre entreprise 
                    possède un numéro d'identification TVA valide. Cette TVA nous sera utile lors de la génération des facturations.
                  </div>
                  {(errors.tva || hasError('tva')) && (
                    <div className="invalid-feedback">
                      {errors.tva?.message || getErrorMessage('tva')}
                    </div>
                  )}
                </div>
              )}

              {shouldShowField('numeroIdentificationFiscale') && (
                <div className="mb-3">
                  <label htmlFor="numeroIdentificationFiscale" className="form-label">
                    Numéro d'Identification Fiscale (NIU) 
                    {isFieldRequired('numeroIdentificationFiscale') && !isMinorWithRepresentative && (
                      <span className="text-danger">*</span>
                    )}
                  </label>
                  <input
                    type="text"
                    {...register("numeroIdentificationFiscale", { 
                      required: isFieldRequired('numeroIdentificationFiscale') && !isMinorWithRepresentative 
                        ? "Ce champ est requis." 
                        : false 
                    })}
                    className={`form-control ${
                      errors.numeroIdentificationFiscale || backendErrors.numeroIdentificationFiscale || hasError('numeroIdentificationFiscale') ? 'is-invalid' : ''
                    } ${watch("numeroIdentificationFiscale") && !hasError('numeroIdentificationFiscale') ? 'is-valid' : ''}`}
                    id="numeroIdentificationFiscale"
                    disabled={commonDisabled}
                  />
                  
                  {isMinorWithRepresentative && (
                    <div className="form-text text-info">
                      <i className="fas fa-info-circle me-1"></i>
                      Pour les mineurs avec représentant légal, le NIU n'est pas obligatoire.
                    </div>
                  )}
                  
                  {(errors.numeroIdentificationFiscale || hasError('numeroIdentificationFiscale')) && (
                    <div className="invalid-feedback">
                      {errors.numeroIdentificationFiscale?.message || getErrorMessage('numeroIdentificationFiscale')}
                    </div>
                  )}
                </div>
              )}

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="preference" className="form-label">
                      Préférence d'investissement
                    </label>
                    <Controller
                      name="preference"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={investmentOptions}
                          placeholder="-- Choisir --"
                          isDisabled={commonDisabled}
                          className={`react-select-container ${
                            errors.preference || hasError('preference') ? 'is-invalid' : ''
                          }`}
                          classNamePrefix="react-select"
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderColor: errors.preference || hasError('preference') ? '#dc3545' : '#182848',
                              minHeight: '38px'
                            })
                          }}
                          value={investmentOptions.find(option => option.value === field.value) || null}
                          onChange={(selected) => field.onChange(selected?.value || '')}
                        />
                      )}
                    />
                    {(errors.preference || hasError('preference')) && (
                      <div className="invalid-feedback d-block">
                        {errors.preference?.message || getErrorMessage('preference')}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">Description</label>
                    <textarea
                      {...register("description")}
                      className={`form-control ${
                        watch("description") && !hasError('description') ? 'is-valid' : ''
                      }`}
                      id="description"
                      disabled={commonDisabled}
                      rows="1"
                    />
                    {watch("description") && !hasError('description') && (
                      <div className="valid-feedback">Description valide</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoPage;